import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@platform/auth/frontend/contexts/AuthContext';
import { useTab } from '@platform/app-shell/frontend/contexts/TabContext';
import { API_BASE } from '@platform/http/api-client';
import UserAvatar from '@platform/ui/UserAvatar';
import '@platform/app-shell/frontend/components/UserAccountMenu/UserAccountMenu.css';

// Resolve relative picture paths against the backend. Required for the
// /api/avatars/:accountId route — without this, the browser resolves
// against the frontend origin, which 404s when it differs from the API.
function resolvePicture(picture) {
  if (!picture) return null;
  if (/^https?:\/\//i.test(picture)) return picture;
  if (picture.startsWith('/')) return `${API_BASE}${picture}`;
  return picture;
}

// Account avatar in the top-right corner of every page header. Click
// reveals a small panel with the user's name + email, a Profile link and
// a Logout button. Closes on outside-click, Escape, or after logout.
// Pulls user + logout from AuthContext directly so callsites just need to
// render <UserAccountMenu /> — no props to thread through.
//
// The avatar is the SAME <UserAvatar> comments, assignees and the people
// pickers render, fed the same seed (account id → colour, display name →
// initials, /api/avatars bytes when uploaded). It used to be a separate
// chip that took its initials from the EMAIL and always sat on the accent
// colour, so the person in the corner never matched the person on their
// own comments.
export default function UserAccountMenu() {
  const { user, logout } = useAuth();
  const { openTab } = useTab();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  // UserAvatar reads the session shape (name + picture) directly; only the
  // relative picture path needs resolving against the API origin.
  const avatarUser = user ? { ...user, displayName: user.name || user.email || null } : null;

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const openProfile = () => {
    setOpen(false);
    openTab('/profile', 'My profile');
  };

  return (
    <div className="account-menu" ref={rootRef}>
      <button
        type="button"
        className={`account-avatar ${open ? 'open' : ''}`}
        onClick={() => setOpen(o => !o)}
        title={user?.name || user?.email || 'Account'}
        aria-haspopup="true"
        aria-expanded={open}
      >
        <UserAvatar user={avatarUser} size={30} resolveImageUrl={resolvePicture} title={user?.name || user?.email || 'Account'} />
      </button>
      {open && (
        <div className="account-menu-dropdown" role="menu">
          <div className="account-menu-header">
            <UserAvatar user={avatarUser} size={40} resolveImageUrl={resolvePicture} />
            <div className="account-menu-identity">
              <span className="account-menu-label">Signed in as</span>
              {user?.name && (
                <span className="account-menu-name" title={user.name}>{user.name}</span>
              )}
              <span className="account-menu-email" title={user?.email}>{user?.email || ''}</span>
            </div>
          </div>
          <div className="account-menu-divider" />
          <button
            type="button"
            className="account-menu-item account-menu-profile"
            role="menuitem"
            onClick={openProfile}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
            <span>Profile</span>
          </button>
          <button
            type="button"
            className="account-menu-item account-menu-logout"
            role="menuitem"
            onClick={() => { setOpen(false); logout?.(); }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
              <polyline points="16 17 21 12 16 7"/>
              <line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
            <span>Logout</span>
          </button>
        </div>
      )}
    </div>
  );
}
